import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

// Sec-S9 (2026-05-26): cookie-based session.  We no longer keep the JWT in
// localStorage; the backend sets it as an HttpOnly `eda_session` cookie at
// login.  A separate non-HttpOnly `eda_csrf` cookie carries a token we
// mirror into the `X-CSRF-Token` header on every state-changing request.
//
// `getToken` is preserved (returns "") only so legacy import sites don't
// crash during the transition.  Nothing should rely on its return value.

const CSRF_COOKIE = "eda_csrf";
const USER_KEY = "eda.user"; // non-sensitive — name/email/role for UI display

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const target = name + "=";
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length));
    }
  }
  return "";
}

export function getToken() {
  // Legacy shim — cookie-only auth from here on.
  return "";
}

export function getStoredUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setSession({ user } = {}) {
  // We no longer persist the JWT in localStorage — only the user object,
  // which the UI reads for the avatar, role pill, and AuthGuard checks.
  // The actual auth is the eda_session cookie set by the backend.
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
}

export function clearSession() {
  localStorage.removeItem(USER_KEY);
  // Belt-and-suspenders cleanup of any pre-migration keys.
  localStorage.removeItem("eda.token");
  localStorage.removeItem("eda.expires_at");
}

export function isTokenExpired() {
  // Cookies handle their own expiry; if the cookie is gone /api/auth/me
  // returns 401 and the on401 handler kicks us back to /login.
  return false;
}

/**
 * Map FastAPI / Pydantic error payloads into a user-readable string.
 */
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) =>
        e && typeof e === "object" && typeof e.msg === "string"
          ? e.msg
          : JSON.stringify(e),
      )
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.detail === "string") {
    if (detail.retryable) return `${detail.detail} (retryable)`;
    return detail.detail;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return JSON.stringify(detail);
}

export function isRetryableError(err) {
  const detail = err?.response?.data?.detail;
  return !!(detail && typeof detail === "object" && detail.retryable === true);
}

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 70000,
  // Send cookies on every cross-origin request so the backend session +
  // CSRF cookies make the trip.  CORS_ORIGINS on the backend must list
  // this app's origin explicitly for the browser to allow this.
  withCredentials: true,
});

// Mirror eda_csrf cookie → X-CSRF-Token header on every state-changing
// request.  Safe (GET/HEAD/OPTIONS) requests are skipped to match the
// backend CSRF middleware.
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});

let on401Handler = null;
export function registerOn401(fn) {
  on401Handler = fn;
}

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      clearSession();
      if (typeof on401Handler === "function") on401Handler();
    }
    return Promise.reject(error);
  },
);
